import classNames from 'classnames';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import { PDFDocumentProxy, PDFProgressData } from 'pdfjs-dist/webpack';
import React, { useEffect, useState } from 'react';
import { useInViewEffect } from 'react-hook-inview';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import * as api from '../api/api';
import pdfLogo from '../assets/img/pdf-tool/logo@2x.png';
import * as selectors from '../dff/dff-selectors';
import { loadUser, mountSelector } from '../dff/dff-slice';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { FilesFilterComponent } from '../pdf-tool-manage/common/files-filter/files-filter-component';
import {
  fetchMounts,
  fetchPdfs,
  resetState,
  selectFilesToMerge,
  selectPdfs,
  toggleMergeSelect,
} from '../pdf-tool-manage/pdf-slice';
import { useAppDispatch } from '../store/store';
import { Toolbar } from '../toolbar/toolbar-component';
import { MainMenuComponent } from '../menu/main-menu';

require('./pdf-tool-merge.scss');

export interface PdfToolStateProps {
  mount: api.Mount;
  user: api.User;
}

export interface PdfToolDispatchProps {
  selectMount: (mount: api.Mount) => void;
  loadUser: (user: api.User) => void;
}

export interface PdfToolOwnProps {}

export interface PdfToolProps
  extends PdfToolStateProps,
    PdfToolDispatchProps,
    PdfToolOwnProps {
  history: any;
}

const PdfFirstPage = ({ file }) => {
  const [inView, setInView] = useState(false);
  const [finishedRendering, setFinishedRendering] = useState(false);

  const mount = useSelector(mountSelector);

  const dispatch = useAppDispatch();

  const ref = useInViewEffect(
    ([entry], observer) => {
      // if (entry.isIntersecting) {
      //   observer.unobserve(entry.target);
      // }
      setInView(entry.isIntersecting);
    },
    { threshold: 0.5 },
  );

  const handleFileClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    f: any,
  ) => {
    e.preventDefault();
    dispatch(
      toggleMergeSelect({
        file: f,
      }),
    );
  };

  const fileNameTruncated = (name: string) => {
    if (name.length > 15) {
      return name.substring(0, 15) + '...';
    }
    return name;
  };

  const cancelLoading = (task: PDFDocumentProxy) => {
    task.destroy();
  };

  useEffect(() => {
    let isSubscribed = true;
    let loadingTask: any;
    if (inView) {
      const url = `/content/api/v2/mounts/${
        file.mountId
      }/files/get?path=${encodeURIComponent(file.path)}`;
      if (isSubscribed) {
        loadingTask = pdfjsLib.getDocument(url);
        loadingTask.onProgress = function (data: PDFProgressData) {
          if (!isSubscribed && !finishedRendering) {
            cancelLoading(loadingTask);
            return;
          }
        };
        const canvas = document.getElementById(
          file.file.name,
        ) as HTMLCanvasElement;
        if (canvas === null) {
          return;
        }

        loadingTask.promise.then((pdf: PDFDocumentProxy) => {
          const firstPagePromise = pdf.getPage(1);

          firstPagePromise.then(
            (page) => {
              const viewport = page.getViewport({ scale: 1 });
              const context = canvas.getContext('2d');
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              // Render PDF page into canvas context
              context.clearRect(0, 0, canvas.width, canvas.height);
              context.beginPath();
              const renderContext = {
                canvasContext: context,
                viewport: viewport,
              };
              page.render(renderContext).promise.then(
                () => {
                  setFinishedRendering(true);
                },
                (e) => {
                  setFinishedRendering(true);
                },
              );
            },
            (e) => {},
          );
        });
      }
    } else {
      if (loadingTask) {
        cancelLoading(loadingTask);
      }
    }

    return () => {
      isSubscribed = false;
    };
  }, [inView, mount]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <div className="pdf-tool-merge__result" ref={ref}>
      <a
        onClick={(e) => handleFileClick(e, file)}
        className="pdf-tool-merge__result-link"
      >
        <div
          className={classNames('pdf-tool-merge__thumbnail-container', {
            'pdf-tool-merge__thumbnail-container--selected': file.selectedMerge,
          })}
        >
          {file.selectedMerge ? (
            <div className="pdf-tool-merge__result-checkmark"></div>
          ) : (
            <div className="pdf-tool-merge__result-checkmark-empty"></div>
          )}
          <div
            className="pdf-tool-merge__thumbnail-image"
            style={{ height: '100%' }}
          >
            <canvas
              id={file.file.name}
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            ></canvas>
          </div>
        </div>
        <div className="pdf-tool-merge__name" data-tip={file.file.name}>
          {fileNameTruncated(file.file.name)}
        </div>
      </a>
    </div>
  );
};

export const PdfToolMergeComponent: React.FC<PdfToolProps> = ({ history }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMounts, setIsLoadingMounts] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [description, setDescription] = useState(
    'Select at least two PDF files you wish to merge.',
  );

  const user = useSelector(selectors.user);
  // TODO: Change this in reducer and add filtered files selector
  const filteredFiles = useSelector(selectPdfs);
  const filesToMerge = useSelector(selectFilesToMerge);

  const dispatch = useAppDispatch();

  useEffect(() => {
    document.title = 'Koofr EXP - PDF Merge';
  }, []);

  useEffect(() => {
    fetch('/api/v2/user/appconfig')
      .then((results) => {
        if (results.ok) {
          return results.json();
        }
        throw new Error(results.status as any);
      })
      .then((data) => {
        dispatch(loadUser(data));
        setIsLoading(false);
      })
      .then(() => {
        dispatch(fetchMounts({})).then(() => {
          dispatch(fetchPdfs({ mountId: null, query: '' })).then(() => {
            setIsLoadingFiles(false);
          });
          setIsLoadingMounts(false);
        });
      })
      .catch((err) => {
        console.error(err);
        setIsLoadingMounts(false);
        setIsLoading(false);
        setIsLoadingFiles(false);
      });
    dispatch(resetState());
  }, []);

  const handleMergeButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (filesToMerge.length > 1) {
      history.push('/pdf-tool/merge/tool/');
    }
  };

  useEffect(() => {
    if (filesToMerge.length >= 2) {
      setDescription('Select all files you wish to merge.');
    } else if (filesToMerge.length === 1) {
      setDescription('Select at least one more PDF to merge.');
    } else {
      setDescription('Select at least two PDF files you wish to merge.');
    }
  }, [filesToMerge.length]);

  return (
    <div className="pdf-tool-merge">
      <Toolbar>
          <div className='l-toolbar__column'>
            <Link to="/">
              <img src={pdfLogo} alt="Koofr PDF Tool logo" className="l-toolbar__logo"/>
            </Link>
            <div className='divider'></div>
            <div className='app-name'><span className="text-normal">PDF Tool &gt;</span> Merge PDF</div>
          </div>
          <div className='l-toolbar__column stretch'></div>
          <div className='l-toolbar__column'>
            <Link to="/pdf-tool/split/" className='gray-link'>Split PDF</Link>
            <div className='divider'></div>
            <MainMenuComponent/>
          </div>
        </Toolbar>
      <ReactTooltip effect="solid" />
      <div className="pdf-tool-merge-container">
        {!isLoading && !user ? (
          <a
            href={`/login?redirect=${encodeURIComponent('/pdf-tool/')}`}
            className="login-button"
          >
            Sign in with Koofr
          </a>
        ) : null}

        {user && user.extra.subscription.isPayingUser ? (
          <FilesFilterComponent></FilesFilterComponent>
        ) : null}

        <div
          className="pdf-tool-merge__docs-container"
          id="docsContainer"
        ></div>

        {filteredFiles.length ? (
          <div className="pdf-tool-merge__results-container">
            <div className="pdf-tool-merge__intro">
              <div className="pdf-tool-merge__text">{description}</div>
            </div>
            {filteredFiles.map((f, index) => (
              <PdfFirstPage key={`page ${f.file.name} ${index}`} file={f} />
            ))}
          </div>
        ) : null}

        <div className="pdf-tool-merge__bottom-bar">
          <div className="pdf-tool-merge__bottom-bar__text">
            Combine PDFs in the order you want.
          </div>
          <button
            className={classNames('pdf-tool-merge__bottom-bar__button', {
              'pdf-tool-merge__bottom-bar__button--disabled':
                filesToMerge.length < 2,
            })}
            onClick={(e) => {
              handleMergeButtonClick(e);
            }}
          >
            {`Merge PDFs (${filesToMerge.length})`}
          </button>
        </div>

        {!isLoading && user && !user.extra.subscription.isPayingUser ? (
          <div className="upgrade">
            <p className="upgrade__text">
              Koofr PDF Tool is only available to paying users.
            </p>
            <a
              href="https://app.koofr.net/app/account"
              className="login-button"
            >
              Upgrade your account
            </a>
          </div>
        ) : null}

        {isLoadingMounts || isLoadingFiles ? <LoadingSpinner /> : null}
      </div>
    </div>
  );
};
